/**
 * brandImportRunner: the BRAND body, on the shared commit sequence.
 * ───────────────────────────────────────────────────────────────────────────
 * The sequencing, the section-id recovery, the upload-once-per-file cache, the
 * extra-angle rule, the run log and the inactive-by-default gate all live in
 * catalogueImportRunner.js. What is left here is the one genuinely
 * brand-specific thing in a commit: WHICH FIELDS the brand catalogue stores
 * and what it calls them.
 *
 * `runBrandImport(plan, deps)` keeps the signature it always had, so
 * BrandImportModal and brandImportRunner.test.js did not have to change.
 *
 * WHY THE BODY IS WORTH ITS OWN FUNCTION. createBrandProduct silently DROPS
 * any body field it does not destructure, so a mis-named key fails by doing
 * nothing rather than by erroring. That is why the tests assert on the exact
 * payload, and why the payload is built in one readable place rather than
 * spread through the commit loop.
 *
 * NEVER SENT: commissionRate, commissionAmount, vendorNetPrice,
 * commissionCalculatedAt, commissionRateSource, country. Server-derived, all
 * six. See brandImport.js for why.
 */
import { runCatalogueImport } from "./catalogueImportRunner.js";

/**
 * The brand product create body.
 *
 * `enabled` is handed in already resolved by the shared runner, which applies
 * the inactive-by-default policy for every vertical at once. Do not re-derive
 * it here.
 */
export const buildBrandProductBody = ({ item, image, additionalImages, enabled }) => {
  const body = {
    name: item.name,
    description: item.description || "",
    /* Founder rule, 29 Sep 2026: no item may sell at 0. A quoted item carries
       price 0 WITH `priceOnRequest` set, never a bare 0, and the validator has
       already refused any row that had a zero price without the posture. */
    price: item.priceOnRequest ? 0 : Math.round(item.price || 0),
    image,
    additionalImages,
    enabled,
    countInStock:
      item.countInStock === null || item.countInStock === undefined
        ? 1
        : Math.max(1, Math.round(item.countInStock)),
    measurements:
      item.measurements === null || item.measurements === undefined
        ? 1
        : item.measurements,
    tags: item.tags,
    category: item.categoryIds,
    requiredPaymentMethod: item.requiredPaymentMethod || "NONE",
    orderFlowOverride: item.orderFlowOverride || "INHERIT",
    priceOnRequest: !!item.priceOnRequest,
    minOrderQty: item.minOrderQty === null ? 1 : Math.round(item.minOrderQty),
  };

  // Optional fields are OMITTED rather than sent empty — the brand forms'
  // house style, and it keeps "" out of trimmed string paths.
  if (item.skuCode) body.skuCode = item.skuCode;
  // Recorded on the product, so a placeholder cover stays findable long
  // after whoever ran the import has forgotten which rows had one.
  if (item.imageSource) body.imageSource = item.imageSource;
  if (item.unitId) body.unit_id = item.unitId;
  if (item.bulkPrice !== null) body.bulkPrice = item.bulkPrice;
  if (item.leadTimeDays !== null) body.leadTimeDays = Math.round(item.leadTimeDays);
  if (Object.keys(item.tierPrices || {}).length) body.tierPrices = item.tierPrices;
  if (Object.keys(item.specs || {}).length) body.specs = item.specs;

  return body;
};

/**
 * @param {object}   plan                 output of brandImport.buildImportPlan
 * @param {object}   deps
 * @param {object}   deps.brand           { _id, name }
 * @param {Function} deps.createSection   (brandId, body) => res
 * @param {Function} deps.fetchSections   (brandId) => res
 * @param {Function} deps.createProduct   (brandId, sectionId, body) => res
 * @param {Function} deps.uploadImage     (file, opts) => { secure_url }
 * @param {boolean}  [deps.forceDisabled] import everything as enabled:false
 * @param {Function} [deps.onProgress]    ({ phase, done, total, label })
 * @returns {Promise<{created, failed, log}>}
 */
export const runBrandImport = async (plan, deps) => {
  const { brand, ...rest } = deps;
  return runCatalogueImport(plan, {
    ...rest,
    vendor: brand,
    buildBody: buildBrandProductBody,
    vendorMedia: {
      vendorType: "brand",
      entityType: "brandProduct",
      source: "dashboard-brand-import",
    },
  });
};

export default runBrandImport;

/* Exported for the tests only. Kept as a re-export so the old import path
   still resolves; the helpers themselves are private to the shared runner. */
export { __internals } from "./catalogueImportRunner.js";
